'use client';
import cn from 'classnames';

import Text from '@/components/common/Text/Text';
import { RawEPCCCartResponse } from '@/types/Cart';
import { InsuranceProduct, SubscriptionProduct } from '@/types/Subscription';
import { SignupState } from '@/types/Signup';

import type { ReferralCopyProps } from '@/components/common/SignUpApp/SignUpApp';
import MemberSummaryPanel from './MemberSummaryPanel';
import SummaryReadout from './SummaryReadout';

type SummaryDockProps = {
  user: SignupState;
  subs: SubscriptionProduct[];
  insuranceData: InsuranceProduct[];
  previewCart?: RawEPCCCartResponse;
  previewCartUpdated: boolean;
  referralCopy?: ReferralCopyProps;
  onCommitValues: (values: Partial<SignupState>) => void;
  /** Whether the full-screen (mobile) / in-column (desktop) editor is open. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * False once the review cards are showing — the "Membership Details" card
   * has its own Edit into this same panel by then, and the resting bar is
   * both a redundant repeat of it and, on mobile, sits over the payment
   * buttons underneath. Desktop is unaffected: it never renders the resting
   * bar in the first place, always showing the editor in the right column.
   */
  showReadout?: boolean;
};

/**
 * The summary in both its states: `SummaryReadout` at rest, and the editable
 * `MemberSummaryPanel` it swaps in for. The readout is the default on every
 * breakpoint — membership values all ship with defaults, so an applicant who
 * never opens the editor still submits a valid membership.
 *
 * Mobile shows the editor as a full-screen overlay; desktop swaps it into the
 * right column in place. Same state drives both.
 */
function SummaryDock(props: SummaryDockProps) {
  const {
    user,
    subs,
    insuranceData,
    previewCart,
    previewCartUpdated,
    referralCopy,
    onCommitValues,
    open: summaryOpen,
    onOpenChange,
    showReadout = true
  } = props;

  return (
    <>
      {/*
        Not rendered while the editor is open — on mobile it would sit behind
        the overlay, and on desktop both would claim the same grid column.
      */}
      {!summaryOpen && showReadout && (
        <SummaryReadout
          user={user}
          onEdit={() => onOpenChange(true)}
        />
      )}

      <aside
        className={cn('SinglePageApplication-summary', {
          expanded: summaryOpen
        })}
      >
        {/*
          Shown on both breakpoints — desktop needs a way back to the readout
          as much as the mobile overlay does. Changes commit as they are made,
          so this only dismisses the editor; there is nothing to save.
        */}
        <div className='SinglePageApplication-summary-header'>
          <Text variant='title-xxs-700-upper'>Membership Summary</Text>
          <button
            type='button'
            className='SinglePageApplication-summary-close cursor'
            onClick={() => onOpenChange(false)}
            data-testid='summary-close'
          >
            {/* "Save" per the design's textlink, even though changes commit
                as they are made — it is the word people expect on a panel they
                opened to change something. */}
            <Text variant='title-xxs-500-upper'>Save</Text>
          </button>
        </div>

        <div className='SinglePageApplication-summary-body'>
          <MemberSummaryPanel
            user={user}
            subs={subs}
            insuranceData={insuranceData}
            previewCart={previewCart}
            previewCartUpdated={previewCartUpdated}
            referralCopy={referralCopy}
            onPlanChange={(plan) => onCommitValues({ plan })}
            onAddonChange={(addon) =>
              onCommitValues({ addon_subscription: addon })
            }
            onCoverageChange={(coverage) =>
              onCommitValues({ insurance_level: coverage })
            }
            onReferralCommit={(referralCode) =>
              onCommitValues({ referralCode })
            }
          />
        </div>
      </aside>
    </>
  );
}

export default SummaryDock;
